import React, { useEffect } from 'react';

interface PrivacyModalProps {
  onClose: () => void;
}

const PrivacyModal: React.FC<PrivacyModalProps> = ({ onClose }) => {
  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Prevent body scroll while open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Privacy Policy"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative glass-card border border-white/15 w-full max-w-lg max-h-[80vh] overflow-y-auto scrollbar-thin p-8">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/30 hover:text-white/80 transition-colors text-xl leading-none"
          aria-label="Close"
        >
          ✕
        </button>

        <h2 className="text-white font-black text-2xl mb-1 ig-gradient-text inline-block">
          Privacy Policy
        </h2>
        <p className="text-white/30 text-xs mb-6">Last updated: {new Date().getFullYear()}</p>

        <div className="space-y-5 text-white/60 text-sm leading-relaxed">
          <section>
            <h3 className="text-white font-semibold mb-1">What we collect</h3>
            <p>
              <strong className="text-white/80">Nothing.</strong> Purgr does not collect, store, or transmit
              any personal data. The files you upload are processed entirely within your browser
              and are never sent to any server. When you close the tab, the data is gone.
            </p>
          </section>

          <section>
            <h3 className="text-white font-semibold mb-1">Advertising (Google AdSense)</h3>
            <p>
              If you accept cookies, we display ads via Google AdSense. Google may use cookies
              and similar technologies to show you personalized ads based on your browsing
              history. This keeps Purgr free to use.
            </p>
            <p className="mt-2">
              You can opt out of personalized ads at{' '}
              <a
                href="https://adssettings.google.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/60 hover:text-white underline"
              >
                adssettings.google.com
              </a>{' '}
              or by declining cookies on this site.
            </p>
          </section>

          <section>
            <h3 className="text-white font-semibold mb-1">Your cookie choice</h3>
            <p>
              We store your cookie preference (accepted or declined) in your browser's
              local storage so we don't ask again. No cookies are set if you decline.
              You can clear this preference at any time by clearing your browser's local storage.
            </p>
          </section>

          <section>
            <h3 className="text-white font-semibold mb-1">Third-party links</h3>
            <p>
              Purgr contains affiliate links and links to third-party services (Ko-fi, Gumroad,
              Instagram growth tools). We are not responsible for the privacy practices of
              those sites.
            </p>
          </section>

          <section>
            <h3 className="text-white font-semibold mb-1">Your Instagram data</h3>
            <p>
              The files you upload (following.json, followers.html, etc.) are read directly
              in your browser. They are <strong className="text-white/80">never uploaded</strong>,
              never stored, and never shared. Purgr has no backend server.
            </p>
          </section>

          <section>
            <h3 className="text-white font-semibold mb-1">Contact</h3>
            <p>
              Questions? Reach us via{' '}
              <a
                href="https://ko-fi.com/riannaelisse"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/60 hover:text-white underline"
              >
                Ko-fi
              </a>.
            </p>
          </section>
        </div>

        <button
          onClick={onClose}
          className="mt-8 w-full btn-gradient text-white font-semibold py-3 rounded-full text-sm"
        >
          Got it
        </button>
      </div>
    </div>
  );
};

export default PrivacyModal;
